class VideoAutoplay {
  constructor() {
    this.observedVideos = new WeakSet();
    this.init();
  }

  init() {
    this.setupIntersectionObserver();
    this.observeExistingVideos();
    this.setupMutationObserver();
  }

  setupIntersectionObserver() {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;

          if (entry.isIntersecting) {
            this.playVideo(video);
          } else {
            this.pauseVideo(video);
          }
        });
      },
      {
        threshold: 0.10,
        rootMargin: '0px'
      }
    );
  }

  setupMutationObserver() {
    this.mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            if (node.tagName === 'VIDEO' && node.getAttribute('data-autoplay') === 'true') {
              this.observeVideo(node);
            }

            const videos = node.querySelectorAll?.('video[data-autoplay="true"]');
            videos?.forEach((video) => this.observeVideo(video));
          }
        });
      });
    });

    this.mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  observeExistingVideos() {
    const videos = document.querySelectorAll('video[data-autoplay="true"]');
    videos.forEach((video) => this.observeVideo(video));
  }

  observeVideo(video) {
    if (this.observedVideos.has(video)) {
      return;
    }

    this.observedVideos.add(video);
    this.intersectionObserver.observe(video);
    this.setupIOSWorkaround(video);
  }

  setupIOSWorkaround(video) {
    const container = video.closest('[data-video-container]') || video.parentElement;

    const touchHandler = () => {
      if (video.paused && this.isVideoInViewport(video)) {
        this.playVideo(video);
      }
    };

    container.addEventListener('touchstart', touchHandler, { passive: true, once: true });
  }

  isVideoInViewport(video) {
    const rect = video.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    const windowWidth = window.innerWidth || document.documentElement.clientWidth;

    const vertInView = (rect.top <= windowHeight) && ((rect.top + rect.height) >= 0);
    const horInView = (rect.left <= windowWidth) && ((rect.left + rect.width) >= 0);

    return (vertInView && horInView);
  }

  playVideo(video) {
    const playPromise = video.play();

    if (playPromise !== undefined) {
      playPromise.catch((error) => {
        console.warn('Video autoplay failed:', error);
      });
    }
  }

  pauseVideo(video) {
    if (!video.paused) {
      video.pause();
    }
  }

  destroy() {
    this.intersectionObserver?.disconnect();
    this.mutationObserver?.disconnect();
  }
}

export default VideoAutoplay;
